// Material corrosion: a single Entropy threshold, read live rather than
// stored -- the same "recomputed every time, never cached" shape
// barrier.reformable and vines' own stage already use. Contrast VinesDef's
// five-stage ladder: this is one on/off crossing, because real corrosion
// doesn't have a climbable "half-rusted" middle state worth modeling. Named
// for where it started (metal rust) and kept that name as it grew to cover
// wood rot too -- the same mechanism either way, just a different word for
// the same kind of decay, the same sense `MassDef.material` already covers
// stone/metal/wood under one "mass" cluster rather than three.
//
// Only two things ever read `properties.entropy` here: which texture and
// description to show (this module), and whether a `conduit.conductive`
// object still actually conducts (queries.isConductive) -- rusting is
// understood physically as an insulating layer forming, not just a
// different-looking metal. Wood is never conductive at any tier to begin
// with, so rotting it only ever changes the second of those two things.
// Reaching Entropy is entirely generic (an ordinary Transfer or Project,
// ContinuousMag or not, ambient or marked-source), the same way vines'
// Energy/Entropy thresholds are -- no cast handler knows `rust` exists.
import { Facing, ObjectDef } from './types';
import { PhysicsConfig, DEFAULT_PHYSICS } from './physics';
import { SimEvents } from './events';
import { WorldState } from './state';
import { StagePresentation, applyStagePresentation } from './presentation';
import { ROCK_TIER_META } from './constants';

export type RustStageId = 'clean' | 'rusted';

export interface RustStageMeta extends StagePresentation {
  /** Required here, unlike on `StagePresentation` itself: corroded metal is a
   * different thing from sound metal, and saying so in the description while
   * leaving the label reading "Metal Block" was the reported bug this ladder's
   * `name` column exists to close. */
  name: string;
}

/** The rusted look and flavor text for each metal tier -- tiers 2 and 3 share
 * one rusted texture, the same shortcut they already share on the clean side of
 * ROCK_TIER_META, and are told apart the same way there too: tier 3 carries
 * `spriteScale` and so draws smaller, in either state. The clean state is read
 * straight off ROCK_TIER_META itself rather than duplicated here, so editing
 * a tier's authored texture/description can't drift the two states apart. */
export const METAL_LADDER_RUSTED: Record<1 | 2 | 3, RustStageMeta> = {
  1: {
    name: 'Rusted Metal Block',
    texture: 'object-block-metal-rusted',
    description: "A block of corroded, rust-scaled metal. Whatever used to run through it doesn't any more -- rust doesn't conduct.",
  },
  2: {
    name: 'Rusted Scrap Metal',
    texture: 'object-scrap-metal-rusted',
    description: "A jagged offcut of metal, eaten through with rust. Whatever it once conducted, it doesn't any more.",
  },
  3: {
    name: 'Rusted Metal Sliver',
    texture: 'object-scrap-metal-rusted',
    description: "A splinter of metal, eaten through with rust. Whatever it once conducted, it doesn't any more.",
  },
};

/** The rotted look and flavor text for each wood tier -- same shortcut as
 * metal's own tiers 2/3 (Split Beam and Kindling already share one clean
 * texture in ROCK_TIER_META, and share one rotted texture here too). Wood
 * was never conductive at any tier to begin with, so rotting it changes
 * nothing about conduction -- purely the same live, reversible look/flavor
 * state metal gets. */
export const WOOD_LADDER_ROTTED: Record<1 | 2 | 3, RustStageMeta> = {
  1: {
    name: 'Rotten Wood Block',
    texture: 'object-block-wood-rotten',
    description: 'A block of soft, rotten wood, crumbling at the edges. Whatever it once blocked, it barely still can.',
  },
  2: {
    name: 'Rotten Split Beam',
    texture: 'object-wood-beam-rotten',
    description: 'A length of timber, rotted through. It would come apart at a touch.',
  },
  3: {
    name: 'Rotten Kindling',
    texture: 'object-wood-beam-rotten',
    description: 'A length of timber, rotted through. It would come apart at a touch.',
  },
};

/** Metal Floor's own clean/rusted pair -- unrelated to ROCK_TIER_META, since a
 * floor tile is walkable terrain, never part of the Separate·Mass ladder. */
export const METAL_FLOOR: Record<RustStageId, RustStageMeta> = {
  clean: {
    name: 'Metal Floor',
    texture: 'object-floor-metal',
    description: 'A section of floor paneled in dense metal. It would carry a charge readily, if anything fed one into it.',
  },
  rusted: {
    name: 'Rusted Metal Floor',
    texture: 'object-floor-metal-rusted',
    description: "A section of floor paneled in corroded, rust-scaled metal. Whatever used to run through it wouldn't any more -- rust doesn't conduct.",
  },
};

/** Wooden Floor's own clean/rotted pair -- never conductive at all (wood
 * never carries `conduit`, at any entropy), so unlike Metal Floor rotting one
 * has no functional consequence yet. Purely the look, for now -- a future
 * mechanic where a rotted floor tile can collapse (dropping through to
 * whatever's below, or opening a Chasm-like hole) is a natural next step
 * this cluster already has everything it needs for (the live entropy read is
 * right here), but was explicitly out of scope for this pass and isn't
 * built. */
export const WOOD_FLOOR: Record<RustStageId, RustStageMeta> = {
  clean: {
    name: 'Wooden Floor',
    texture: 'object-floor-wood',
    description: 'A section of floor, planked in dry, sturdy wood.',
  },
  rusted: {
    name: 'Rotten Wooden Floor',
    texture: 'object-floor-wood-rotten',
    description: 'A section of floor, planked in soft, rotten wood. It gives slightly underfoot.',
  },
};

/** The Generator Mount's own clean/rusted pair. Mechanically it is a Metal
 * Floor and then some (see its catalog entry: everything a Metal Floor does,
 * plus `sourceMount`), and it borrows the same two textures -- but it is not
 * *called* a Metal Floor, and until 2026-08-11 it carried `kind:
 * 'metal-floor'`, so the first thing that touched its Entropy overwrote "a
 * metal panel cut with a shallow cradle, bolt-holes and all" with "a section of
 * floor paneled in dense metal", permanently. The mount is the one seat in the
 * world a relocated Charge source can take, and rusting it once cost the world
 * the only sentence that said so.
 *
 * Which is the argument for a family per *named thing* rather than per texture:
 * the ladder owns the object's whole presentation, so two objects that share a
 * sprite and differ in what they are need two rows, not one. */
export const GENERATOR_MOUNT: Record<RustStageId, RustStageMeta> = {
  clean: {
    name: 'Generator Mount',
    texture: 'object-floor-metal',
    description: 'A metal panel cut with a shallow cradle, bolt-holes and all -- shaped to take something heavy and leave it seated.',
  },
  rusted: {
    name: 'Rusted Generator Mount',
    texture: 'object-floor-metal-rusted',
    description: 'The cradle is still cut into the panel, but the panel is rust-scaled through. It would still seat something heavy; it would carry nothing out of it.',
  },
};

/** Cabling's own clean/corroded pair.
 *
 * The rows live here with every other rust family, so `rustFamily` stays the one
 * dispatch and the Encyclopedia's ladder (`sim/ladder.ts`) reads a cable the
 * same way it reads a Metal Block. What is different is that the *texture* named
 * here is only the straight east-west run: a cable's look is its corrosion stage
 * crossed with which of its four neighbours it is actually joined to, and that
 * second half is a question about the room rather than about the object. So
 * `sim/cable.ts` owns a cable's whole presentation (it swaps in the piece that
 * matches its neighbours, in whichever of these two palettes its Entropy puts
 * it), `settleRust` skips it, and these rows stay the authority on what a cable
 * is *called* and what it reads as. */
export const CABLE: Record<RustStageId, RustStageMeta> = {
  clean: {
    name: 'Cabling',
    texture: 'object-cable-ew',
    description: 'Heavy insulated cable, pinned flat to the floor and running off to somewhere else. Sound enough to carry whatever is put into it.',
  },
  rusted: {
    name: 'Corroded Cabling',
    texture: 'object-cable-rusted-ew',
    description: "Cable eaten through where the sheath has gone. The run is still lying there; nothing is getting past this stretch of it.",
  },
};

/** The Circuit Breaker's own sound/corroded pair, in the **closed** position.
 *
 * Same arrangement as `CABLE` above and for the same reason: the rows live here
 * with every other family so `rustFamily` stays one dispatch, but a breaker's
 * look is its corrosion stage crossed with its switch position, and only
 * `sim/breaker.ts` knows the second. So these are the closed-position rows and
 * that module supplies the open ones, `settleRust` skips a breaker, and the
 * Encyclopedia's ladder still finds something true here rather than dressing a
 * breaker as a Metal Block. */
export const BREAKER: Record<RustStageId, RustStageMeta> = {
  clean: {
    name: 'Circuit Breaker',
    texture: 'object-breaker',
    description: 'A heavy blade switch in a ceramic housing, thrown closed. The line runs straight through it.',
  },
  rusted: {
    name: 'Corroded Circuit Breaker',
    texture: 'object-breaker-rusted',
    description: 'The contacts are scaled over with rust. The blade still closes; nothing crosses it when it does.',
  },
};

/** The conveyor belt's own sound/seized pair, **per direction of travel**.
 *
 * The third family whose look is its corrosion stage crossed with something
 * else, and the only one this module can resolve by itself: a cable's second
 * half is which neighbours it joins (a question about the room) and a breaker's
 * is its switch position (a flag written elsewhere), where a belt's is
 * `conveyor.direction`, which is right here on the object. So `rustFamily` picks
 * the row and `settleRust` dresses a belt exactly as it dresses a Metal Block --
 * no second owner of `obj.texture`, and reversing a run at its console repaints
 * every arrow in it on the settle that follows.
 *
 * The description carries the direction in words as well as in the sprite,
 * because which way a belt runs is the whole of what a player has to read off it
 * and an arrow on a 32-pixel tile is a thin place to say it twice.
 *
 * Seized rather than merely corroded: a rusted belt drops out of its run
 * (`sim/conveyor.ts`), so the break in the line is drawn where the break in the
 * machine is -- the same thing rust already does to a cable, which is the
 * argument for spending Entropy on one. */
const CONVEYOR_HEADING: Record<Facing, string> = { up: 'north', right: 'east', down: 'south', left: 'west' };

export const CONVEYOR: Record<Facing, Record<RustStageId, RustStageMeta>> = Object.fromEntries(
  (['up', 'right', 'down', 'left'] as Facing[]).map((direction) => [
    direction,
    {
      clean: {
        name: 'Conveyor Belt',
        texture: `object-conveyor-${direction}`,
        description: `A slatted belt running flush with the floor, its rollers set to carry ${CONVEYOR_HEADING[direction]}. It turns when the line feeding its console is live, and not otherwise.`,
      },
      rusted: {
        name: 'Seized Conveyor Belt',
        texture: `object-conveyor-rusted-${direction}`,
        description: `Rollers scaled solid with rust, the slats fused to them. It still points ${CONVEYOR_HEADING[direction]}; nothing has moved along this stretch in a long time, and nothing will.`,
      },
    },
  ]),
) as Record<Facing, Record<RustStageId, RustStageMeta>>;

/** The conveyor console's own sound/corroded pair.
 *
 * An ordinary one-object family like the Generator Mount's -- the console's
 * *powered* look is its `litTexture`, which `checkGateThreshold` swaps in for
 * any charged `conduit.feed` and which therefore costs this module nothing. A
 * corroded console cannot read charged at all (rust stops it conducting, so its
 * own network is only itself), so the two never disagree about whether the lamp
 * in its housing is lit. */
export const CONVEYOR_CONSOLE: Record<RustStageId, RustStageMeta> = {
  clean: {
    name: 'Conveyor Console',
    texture: 'object-conveyor-console',
    description: 'A waist-high control post with a single reversing lever, cabling running into its foot. It drives whatever belt it is standing against -- and only what it is standing against.',
  },
  rusted: {
    name: 'Corroded Conveyor Console',
    texture: 'object-conveyor-console-rusted',
    description: 'The housing is eaten through and the terminals under it are scaled over. The lever still throws; nothing reaches the belt when it does.',
  },
};

/** The hand tool's own clean/corroded pair. Metal and tier-2 mass like Scrap
 * Metal, but it wears its own wrench silhouette, so the tier tables would dress
 * it as an offcut -- the same reason Metal Floor has a pair of its own. */
export const METAL_TOOL: Record<RustStageId, RustStageMeta> = {
  clean: {
    name: 'Metal Tool',
    texture: 'object-metal-tool',
    description: 'A hand tool of dense, cool metal, still sound enough to be worth something. Heavy for its size.',
  },
  rusted: {
    name: 'Corroded Metal Tool',
    texture: 'object-metal-tool-rusted',
    description: 'A hand tool, corroded past use but still clearly metal, and still clearly heavy for its size.',
  },
};

/** Which stage an object's current Entropy puts it in. Pure, and cheap enough
 * to call on every settle rather than caching -- the object's own
 * `properties.entropy` is the only state that matters. */
export function rustStage(obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): RustStageId {
  const entropy = obj.properties?.entropy ?? 0;
  return entropy >= physics.metal.rustEntropy ? 'rusted' : 'clean';
}

/** Both stages of whichever family this object belongs to.
 *
 * The whole pair rather than just the current stage, because two callers want
 * different things from the same dispatch: `refreshRustTexture` wants the stage
 * the object is in now, and the Encyclopedia's ladder adapter (`sim/ladder.ts`)
 * wants both rows of the table. That adapter used to carry its own copy of this
 * `kind` switch, which is exactly the drift `ladder.ts`'s own header warns
 * about -- it would have listed a Generator Mount as a Metal Floor. */
export function rustFamily(obj: ObjectDef): Record<RustStageId, RustStageMeta> {
  const kind = obj.rust?.kind ?? 'metal-ladder';
  if (kind === 'metal-floor') return METAL_FLOOR;
  if (kind === 'wood-floor') return WOOD_FLOOR;
  if (kind === 'metal-tool') return METAL_TOOL;
  if (kind === 'generator-mount') return GENERATOR_MOUNT;
  if (kind === 'cable') return CABLE;
  if (kind === 'breaker') return BREAKER;
  // The one family whose row depends on another cluster's field. Right for the
  // same reason `rust.tier` is read here: it is a fact about this object, and
  // the alternative is a second pass that owns a belt's texture and fights this
  // one for it every settle (which is exactly why a cable and a breaker are
  // skipped below rather than resolved here).
  if (kind === 'conveyor') return CONVEYOR[obj.conveyor?.direction ?? 'right'];
  if (kind === 'conveyor-console') return CONVEYOR_CONSOLE;
  const wood = kind === 'wood-ladder';
  const tier = obj.rust?.tier ?? 1;
  // The clean side of a tier ladder is read straight off ROCK_TIER_META rather
  // than duplicated here, so editing a tier's authored look cannot drift the
  // two states apart.
  const clean = ROCK_TIER_META[wood ? 'wood' : 'metal'][tier];
  return {
    clean: { name: clean.name, texture: clean.texture, description: clean.description },
    rusted: wood ? WOOD_LADDER_ROTTED[tier] : METAL_LADDER_RUSTED[tier],
  };
}

function rustMeta(obj: ObjectDef, physics: PhysicsConfig): RustStageMeta {
  return rustFamily(obj)[rustStage(obj, physics)];
}

/** Re-derives this object's name, texture and Inspect description from the
 * stage its Entropy currently reads -- see `presentation.ts` for why all three
 * move together and never separately. Called after every settle.
 *
 * The name is the part added 2026-08-11, and rust is the clearest case of why:
 * "A block of corroded, rust-scaled metal" under a label reading "Metal Block"
 * is the world contradicting itself about the object the player is standing in
 * front of, and every rust object had that. */
export function refreshRustTexture(obj: ObjectDef, physics: PhysicsConfig, out: SimEvents): void {
  if (!obj.rust) return;
  applyStagePresentation(obj, rustMeta(obj, physics), out);
}

/** Objects whose corrosion is only *half* of what they look like, so this pass
 * has to leave them to whichever system knows the other half.
 *
 * Not an exemption from rust: both corrode on exactly this threshold, stop
 * conducting exactly as any other metal does, and wear the two looks their own
 * `rustFamily` row names. What they cannot have is **two owners of
 * `obj.texture`** -- both this pass and theirs write it, so the sound-and-closed
 * piece would be swapped in here and swapped back there on every settle,
 * forever.
 *
 * - A **cable**'s look is its stage crossed with which neighbours it joins
 *   (`sim/cable.ts`, sixteen pieces per palette).
 * - A **breaker**'s is its stage crossed with its switch position
 *   (`sim/breaker.ts`).
 * - A **conveyor belt**'s is its stage crossed with which way its run carries
 *   it (`sim/conveyor.ts`). It was dressed here until 2026-08-16, when a belt's
 *   heading stopped being authored on the tile and started being derived from
 *   the path its run makes -- which moved the second half of the answer off the
 *   object and into the room, and so moved the belt into this list.
 *
 * Every second half is a question about something this module does not have --
 * the room, and a persisted flag -- which is the real reason the split falls
 * here rather than anywhere else. */
function ownsItsOwnLook(obj: ObjectDef): boolean {
  // Asked as "which of my families does somebody else dress", rather than by
  // importing each of their predicates -- `sim/conveyor.ts` imports *this*
  // module for its table and its stage, so reaching back for `isBelt` would
  // close a cycle for one boolean. The rust kind is what picks the table, which
  // makes it exactly the right thing for a question about who owns the texture.
  return obj.conduit?.cable === true || obj.breaker !== undefined || obj.rust?.kind === 'conveyor';
}

/** The declared system's own body (see settle.ts): re-derives every rust
 * object's name, texture and description for whatever just changed. */
export function settleRust(world: WorldState, out: SimEvents): void {
  const room = world.rooms[world.player.roomId];
  if (!room) return;
  for (const obj of room.objects) {
    if (obj.rust && !ownsItsOwnLook(obj)) refreshRustTexture(obj, world.physics, out);
  }
}
